import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowUpRight } from "lucide-react";
import { PageHeader } from "./fordon";
import { HubEditorial, faqSchema } from "@/components/HubEditorial";
import { b96Faq, b96Sections } from "@/data/nationella-guider-2";

const URL = "https://www.akhusbilfritid.se/b96-korkort";

export const Route = createFileRoute("/b96-korkort")({
  // FAQ-schemat byggs i loadern så guidens textdata hamnar i sidans egen
  // chunk i stället för i initial-bundlen.
  codeSplitGroupings: [["loader", "component"]],
  loader: () => ({ faqLd: faqSchema(b96Faq) }),
  head: ({ loaderData }) => ({
    meta: [
      { title: "B96-körkort 2026 – vikter, kostnad, prov & husbilsreglerna" },
      {
        name: "description",
        content:
          "Allt om B96: exakta viktgränser mot B och BE, vad utbildningen och provet kostar 2026, vad som testas — och varför B96 inte hjälper dig köra tyngre husbil.",
      },
      {
        name: "keywords",
        content:
          "b96 körkort, b96 vikt, b96 kostnad, b96 husvagn, be körkort husvagn, körkort husbil 3500 kg",
      },
      { property: "og:type", content: "article" },
      { property: "og:title", content: "B96-körkort 2026 – vikter, kostnad, prov & husbilsreglerna" },
      {
        property: "og:description",
        content:
          "Viktgränserna mot B och BE, kostnad och prov 2026, plus den vanligaste missuppfattningen om husbil och B96.",
      },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          "@id": `${URL}#article`,
          headline: "B96-körkort – vikter, kostnad, prov och husbilsreglerna",
          url: URL,
          inLanguage: "sv-SE",
          about: "B96-behörighet för bil med släp i Sverige",
          author: { "@type": "Organization", name: "AK Husbil & Fritid" },
          publisher: { "@type": "Organization", name: "AK Husbil & Fritid" },
        }),
      },
      ...(loaderData ? [{ type: "application/ld+json", children: JSON.stringify(loaderData.faqLd) }] : []),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Hem", item: "https://www.akhusbilfritid.se/" },
            { "@type": "ListItem", position: 2, name: "Guider", item: "https://www.akhusbilfritid.se/guider" },
            { "@type": "ListItem", position: 3, name: "B96-körkort", item: URL },
          ],
        }),
      },
    ],
  }),
  component: B96Page,
});

const facts = [
  { k: "3 500–4 250 kg", v: "kombinerad totalvikt som B96 täcker" },
  { k: "7 timmar", v: "obligatorisk utbildning, inget teoriprov" },
  { k: "6 000–8 000 kr", v: "total kostnad inklusive provavgift 2026" },
  { k: "3 500 kg", v: "husbilsgräns med B — B96 ändrar inget där" },
];

function B96Page() {
  return (
    <>
      <PageHeader
        label="Nationell guide · Körkort"
        title={
          <>
            B96 — <span className="font-italic-display text-accent">exakt</span> var gränsen går.
          </>
        }
        sub="Viktgränserna mot B och BE, vad utbildningen och provet kostar, vad som faktiskt testas — och den missuppfattning som får husbilsköpare att köpa fel bil."
      />

      <section className="container-edge pt-14 md:pt-20">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {facts.map((f) => (
            <div key={f.k} className="rounded-3xl border border-border bg-card p-6">
              <p className="font-display text-3xl leading-none text-accent">{f.k}</p>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.v}</p>
            </div>
          ))}
        </div>
      </section>

      <div className="pt-14 md:pt-20">
        <HubEditorial eyebrow="Guiden" sections={b96Sections} faq={b96Faq} />
      </div>

      <section className="container-edge pb-20 md:pb-24">
        <div className="rounded-3xl border border-border bg-warm p-7 md:p-10">
          <p className="eyebrow">Läs vidare</p>
          <div className="mt-5 grid gap-3 sm:grid-cols-3">
            <Link
              to="/verktyg/korkort-husbil"
              className="group flex items-center justify-between gap-3 rounded-2xl border border-border bg-card p-5 transition hover:border-accent"
            >
              <span className="text-sm font-medium">Räkna ut din behörighet med vårt verktyg</span>
              <ArrowUpRight size={16} className="shrink-0 text-accent transition group-hover:translate-x-0.5" />
            </Link>
            <Link
              to="/husbilsforsakring"
              className="group flex items-center justify-between gap-3 rounded-2xl border border-border bg-card p-5 transition hover:border-accent"
            >
              <span className="text-sm font-medium">Husbilsförsäkring — kostnad och villkor</span>
              <ArrowUpRight size={16} className="shrink-0 text-accent transition group-hover:translate-x-0.5" />
            </Link>
            <Link
              to="/husvagnar-till-salu"
              className="group flex items-center justify-between gap-3 rounded-2xl border border-border bg-card p-5 transition hover:border-accent"
            >
              <span className="text-sm font-medium">Husvagnar i lager — vi räknar vikten med dig</span>
              <ArrowUpRight size={16} className="shrink-0 text-accent transition group-hover:translate-x-0.5" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
